/*
  Neroli — collection.js
  Filtering, sorting and pagination for both the collection page and the
  search page. Vanilla JS, no dependencies.

  Both sections expose the same hooks, so this one file drives both:
    [data-section-id]          section root — used by the Section Rendering API
    [data-collection-results]  the swappable results region
    [data-product-grid]        the grid that Load More appends into
    [data-collection-toolbar]  count + sort, re-rendered so the count stays true
    [data-filters-panel]       facets panel, re-rendered so filter counts stay true
*/

(() => {
  const resultsContainer = document.querySelector('[data-collection-results]');
  if (!resultsContainer) return;

  /* The section root carries data-section-id. Shopify's own generated
     .shopify-section wrapper does not, so resolving the id by class name
     silently yields undefined and every request degrades to a full page
     load. Walk up from the results region instead — that element is always
     inside the section root on both templates. */
  const sectionRoot = resultsContainer.closest('[data-section-id]');
  const sectionId = sectionRoot ? sectionRoot.dataset.sectionId : null;

  const filtersPanel = document.querySelector('[data-filters-panel]');
  const basePath = window.location.pathname;

  /* Screen readers get no signal from an innerHTML swap, so results changes
     are announced explicitly. Created here rather than in Liquid so the
     markup stays identical across both sections. */
  const liveRegion = document.createElement('p');
  liveRegion.className = 'visually-hidden';
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  document.body.appendChild(liveRegion);

  function announce(message) {
    liveRegion.textContent = '';
    window.setTimeout(() => {
      liveRegion.textContent = message;
    }, 100);
  }

  function withSectionId(url) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}section_id=${encodeURIComponent(sectionId)}`;
  }

  async function fetchDocument(url) {
    const response = await fetch(withSectionId(url));
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  function swapRegion(doc, selector, current) {
    const incoming = doc.querySelector(selector);
    if (incoming && current) current.innerHTML = incoming.innerHTML;
  }

  function productCount() {
    const grid = document.querySelector('[data-product-grid]');
    return grid ? grid.children.length : 0;
  }


  /* The sticky toolbar paints a strip over the offset left by a hidden header,
     but only while it is actually stuck — otherwise the strip floats over the
     content above it. CSS cannot detect a stuck sticky element, so a zero-height
     sentinel just above the toolbar is observed instead. */
  (() => {
    const sentinel = document.querySelector('[data-toolbar-sentinel]');
    const toolbar = document.querySelector('[data-collection-toolbar]');
    if (!sentinel || !toolbar || !('IntersectionObserver' in window)) return;

    const offset =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--neroli-sticky-offset'),
        10
      ) || 0;

    new IntersectionObserver(
      ([entry]) => toolbar.classList.toggle('is-stuck', !entry.isIntersecting),
      { rootMargin: `-${offset + 1}px 0px 0px 0px`, threshold: 0 }
    ).observe(sentinel);
  })();

  /* ---- Mobile filter drawer ----
     The same element is the desktop sidebar and the mobile drawer, so the
     dialog semantics are applied on open and removed on close rather than
     baked into the markup — role="dialog" on a static desktop sidebar would
     be a lie to assistive tech. */

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const overlay = document.querySelector('[data-filters-overlay]');
  let drawerReturnFocus = null;

  function isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  function drawerKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeFilterDrawer();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = Array.from(filtersPanel.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openFilterDrawer(trigger) {
    filtersPanel.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    if (isDesktop()) return;

    drawerReturnFocus = trigger || null;
    filtersPanel.setAttribute('role', 'dialog');
    filtersPanel.setAttribute('aria-modal', 'true');
    if (overlay) overlay.hidden = false;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', drawerKeydown);

    const close = filtersPanel.querySelector('[data-close-filters]');
    if (close) close.focus();
  }

  function closeFilterDrawer() {
    if (!filtersPanel || !filtersPanel.classList.contains('is-open')) return;

    filtersPanel.classList.remove('is-open');
    filtersPanel.removeAttribute('role');
    filtersPanel.removeAttribute('aria-modal');
    if (overlay) overlay.hidden = true;
    document.body.style.overflow = '';
    document.removeEventListener('keydown', drawerKeydown);

    document.querySelectorAll('[data-open-filters]').forEach((button) =>
      button.setAttribute('aria-expanded', 'false')
    );

    // Returning focus to the trigger is what stops a keyboard user being
    // dumped at the top of the document every time they close the drawer.
    if (drawerReturnFocus && document.contains(drawerReturnFocus)) {
      drawerReturnFocus.focus();
    }
    drawerReturnFocus = null;
  }

  /* Resizing past the breakpoint with the drawer open would otherwise leave
     the page scroll-locked with no visible way to release it. */
  window.addEventListener('resize', () => {
    if (isDesktop()) closeFilterDrawer();
  }, { passive: true });

  /* ---- Applying filters and sort ---- */

  async function fetchAndSwap(url, pushState = true) {
    if (!sectionId) {
      window.location.href = url;
      return;
    }

    resultsContainer.setAttribute('aria-busy', 'true');
    if (filtersPanel) filtersPanel.setAttribute('aria-busy', 'true');

    try {
      const doc = await fetchDocument(url);

      swapRegion(doc, '[data-collection-results]', resultsContainer);
      swapRegion(doc, '[data-filters-panel]', filtersPanel);
      swapRegion(
        doc,
        '[data-collection-toolbar]',
        document.querySelector('[data-collection-toolbar]')
      );

      if (pushState) {
        window.history.pushState({ neroliCollection: true }, '', url);
      }

      restoreViewMode();
      document.dispatchEvent(new CustomEvent('neroli:facets:rendered'));
      document.dispatchEvent(new CustomEvent('neroli:products:rendered'));
      announce(`${productCount()} products shown`);
    } catch (error) {
      window.location.href = url;
    } finally {
      resultsContainer.removeAttribute('aria-busy');
      if (filtersPanel) filtersPanel.removeAttribute('aria-busy');
    }
  }

  window.addEventListener('facets:update', (event) => {
    if (event.detail && event.detail.url) fetchAndSwap(event.detail.url);
  });

  /* Back/forward after filtering. Re-fetches rather than trusting bfcache,
     because pushState only ever changed the query string — the document in
     the history entry still holds the previous result set. The pathname
     guard leaves genuine cross-page navigation to the browser. */
  window.addEventListener('popstate', () => {
    if (window.location.pathname !== basePath) return;
    fetchAndSwap(window.location.pathname + window.location.search, false);
  });

  /* ---- Load more ---- */

  async function appendNextPage(url) {
    if (!sectionId) {
      window.location.href = url;
      return;
    }

    const currentGrid = document.querySelector('[data-product-grid]');
    const currentPagination = document.querySelector('[data-pagination]');
    const trigger = currentPagination
      ? currentPagination.querySelector('[data-load-more]')
      : null;

    if (trigger) {
      trigger.classList.add('is-loading');
      trigger.setAttribute('aria-busy', 'true');
    }

    try {
      const doc = await fetchDocument(url);

      const incomingGrid = doc.querySelector('[data-product-grid]');
      const countBefore = currentGrid ? currentGrid.children.length : 0;

      if (incomingGrid && currentGrid) {
        currentGrid.insertAdjacentHTML('beforeend', incomingGrid.innerHTML);
      }

      /* Replacing the pagination block destroys the button that was focused,
         which would drop keyboard focus to <body> and lose the reading
         position. Move focus to the replacement button, or to the last card
         appended when this was the final page. */
      const incomingPagination = doc.querySelector('[data-pagination]');
      if (currentPagination) {
        if (incomingPagination) {
          currentPagination.replaceWith(incomingPagination);
          const nextTrigger = incomingPagination.querySelector('[data-load-more]');
          if (nextTrigger) nextTrigger.focus();
        } else {
          currentPagination.remove();
          const lastCard = currentGrid ? currentGrid.lastElementChild : null;
          if (lastCard) {
            lastCard.setAttribute('tabindex', '-1');
            lastCard.focus();
          }
        }
      }

      document.dispatchEvent(new CustomEvent('neroli:products:rendered'));

      const added = (currentGrid ? currentGrid.children.length : 0) - countBefore;
      announce(`${added} more products loaded. ${productCount()} products shown.`);
    } catch (error) {
      window.location.href = url;
    }
  }

  /* ---- Delegated handlers ----
     Delegated rather than bound, because the toolbar, pagination and facets
     panel are all replaced by innerHTML swaps above. Direct bindings would
     survive exactly one filter change. */

  document.addEventListener('click', (event) => {
    const loadMore = event.target.closest('[data-load-more]');
    if (loadMore) {
      event.preventDefault();
      appendNextPage(loadMore.getAttribute('href'));
      return;
    }

    const facetLink = event.target.closest('[data-facets-ajax-link]');
    if (facetLink) {
      event.preventDefault();
      fetchAndSwap(facetLink.getAttribute('href'));
      return;
    }

    const filterToggle = event.target.closest('[data-open-filters]');
    if (filterToggle && filtersPanel) {
      /* On desktop this button only appears once the sidebar has been hidden,
         so its job there is to bring the sidebar back rather than to open the
         mobile drawer. */
      const shell = document.querySelector('[data-collection-shell]');
      if (isDesktop() && shell && shell.classList.contains('filters-hidden')) {
        shell.classList.remove('filters-hidden');
        return;
      }
      openFilterDrawer(filterToggle);
      return;
    }

    if (event.target.closest('[data-close-filters]') || event.target.closest('[data-filters-overlay]')) {
      closeFilterDrawer();
      return;
    }

    /* Grid / list view. Persisted so the choice survives a filter change,
       which re-renders the toolbar and would otherwise reset the buttons. */
    const viewButton = event.target.closest('[data-view-mode]');
    if (viewButton) {
      applyViewMode(viewButton.dataset.viewMode);
      try {
        window.localStorage.setItem('neroli:collection-view', viewButton.dataset.viewMode);
      } catch (error) {
        /* Private browsing can refuse storage; the toggle still works for
           this page view, it just won't be remembered. */
      }
    }
  });

  function applyViewMode(mode) {
    const grid = document.querySelector('[data-product-grid]');
    if (grid) grid.classList.toggle('collection-grid--list', mode === 'list');
    document.querySelectorAll('[data-view-mode]').forEach((button) => {
      const active = button.dataset.viewMode === mode;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }

  function restoreViewMode() {
    let stored = null;
    try {
      stored = window.localStorage.getItem('neroli:collection-view');
    } catch (error) {
      stored = null;
    }
    if (stored) applyViewMode(stored);
  }

  restoreViewMode();
})();

/* ---------------------------------------------------------------------------
   Moved out of the snippets that own this markup.

   Shopify only processes {% javascript %} and {% stylesheet %} inside SECTION
   files. In a snippet both blocks are discarded silently — no error, no
   warning, the code simply never reaches the page. All of the behaviour below
   lived in snippets and had therefore never run: filter checkboxes did
   nothing, so the browser fell back to a native form GET whose parameters did
   not match what the AJAX path would have built, and the price slider never
   synced to its number inputs.

   Both collection and search sections already load this file, which is why it
   is the destination rather than a new asset.
--------------------------------------------------------------------------- */

/* ---- from snippets/facets.liquid ---- */
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-facets-form]');
      if (!this.form) return;

      this.debouncedApply = this.debounce(() => this.applyFilters(), 500);

      // Checkboxes apply immediately — debouncing a single click just makes
      // the interface feel broken. Only the price fields need the delay, so
      // filters aren't refetched on every keystroke.
      this.form.addEventListener('change', (event) => {
        // Number and range inputs are both handled by the price slider path
        // below; letting them through here would fire a second request.
        if (event.target.type === 'number' || event.target.type === 'range') return;
        this.applyFilters();
      });
      this.form.addEventListener('input', (event) => {
        if (event.target.type !== 'number') return;
        this.debouncedApply();
      });
    }

    applyFilters() {
      const params = new URLSearchParams();

      /* Price is only submitted when the shopper has actually engaged with it,
         either by moving the slider now or because a price filter is already
         applied to this page.

         Shopify recalculates the price facet's range against the CURRENT
         result set, so after any filter the slider re-renders bounded to that
         subset — Energising oils start at $27, so the track now starts at $27.
         Those inputs sit in the same form, so the next filter action submitted
         a $27 floor the shopper never chose, and every action after that
         narrowed the range again. Reading the form wholesale is what turned a
         recalculated bound into a filter. */
      const priceRoot = this.form.querySelector('[data-price-slider]');
      const priceEngaged = Boolean(
        priceRoot && (priceRoot.dataset.priceActive === 'true' || priceRoot.dataset.priceTouched === 'true')
      );

      for (const [key, value] of new FormData(this.form).entries()) {
        if (!value) continue;
        if (key.indexOf('filter.v.price.') === 0 && !priceEngaged) continue;
        params.append(key, value);
      }
      /* Only carry a sort the shopper actually chose. Sending the select's
         current value unconditionally puts the default into every filter URL,
         and the default on a filtered collection is 'relevance' — a sort that
         only means something when there is something to rank against. On a
         plain collection URL it makes Shopify return a partial result set, so
         clearing the last filter appeared to break the page. */
      const sort = document.querySelector('[data-sort-select]');
      if (sort && sort.value && sort.value !== sort.dataset.defaultSort) {
        params.set('sort_by', sort.value);
      }

      const query = params.toString();
      const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
      window.dispatchEvent(new CustomEvent('facets:update', { detail: { url } }));
    }

    debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), wait);
      };
    }
  }

  if (!customElements.get('facet-filters')) {
    customElements.define('facet-filters', FacetFilters);
  }

  /* Price slider. The handles write into the number inputs and dispatch a
     bubbling input event, so the existing debounced apply path stays the only
     thing that talks to the network. */
  function syncPriceSlider(root, commit) {
    const minThumb = root.querySelector('[data-price-thumb="min"]');
    const maxThumb = root.querySelector('[data-price-thumb="max"]');
    const fill = root.querySelector('[data-price-fill]');
    const numbers = root.querySelectorAll('input[type="number"]');
    if (!minThumb || !maxThumb) return;

    const floor = Number(minThumb.min);
    const ceiling = Number(minThumb.max);
    let low = Number(minThumb.value);
    let high = Number(maxThumb.value);

    // Handles must not cross over each other.
    if (low > high) {
      if (document.activeElement === minThumb) high = low;
      else low = high;
      minThumb.value = String(low);
      maxThumb.value = String(high);
    }

    // Both inputs span the full track and overlap. Without swapping z-index the
    // upper one always wins the hit test, so a handle dragged to the far end can
    // never be picked up again.
    const midpoint = floor + (ceiling - floor) / 2;
    minThumb.style.zIndex = low > midpoint ? '4' : '3';
    maxThumb.style.zIndex = low > midpoint ? '3' : '4';

    if (fill && ceiling > floor) {
      const span = ceiling - floor;
      fill.style.left = `${((low - floor) / span) * 100}%`;
      fill.style.right = `${100 - ((high - floor) / span) * 100}%`;
    }

    if (numbers.length === 2) {
      /* A tolerance rather than a strict comparison. The thumbs step in whole
         units while the bounds come from real prices, so a ceiling of 54.99
         leaves the thumb resting on 54 — strictly less than the ceiling, which
         would write a max into the form and silently apply a price filter the
         shopper never set. That filter then narrows every subsequent request
         and, having no chip, is invisible. Anything under a currency unit from
         the bound counts as "not set". */
      numbers[0].value = low - floor > 0.999 ? String(low) : '';
      numbers[1].value = ceiling - high > 0.999 ? String(high) : '';
      if (commit) numbers[1].dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  document.addEventListener('input', (event) => {
    const thumb = event.target.closest('[data-price-thumb]');
    if (!thumb) return;
    const root = thumb.closest('[data-price-slider]');
    /* Records that this came from the shopper. Without it there is no way to
       tell a bound the shopper set from one Shopify recalculated. */
    if (root) root.dataset.priceTouched = 'true';
    syncPriceSlider(root, false);
  });

  document.addEventListener('change', (event) => {
    const thumb = event.target.closest('[data-price-thumb]');
    if (!thumb) return;
    const root = thumb.closest('[data-price-slider]');
    if (root) root.dataset.priceTouched = 'true';
    syncPriceSlider(root, true);
  });

  /* The number inputs are the real form fields and the keyboard path, so
     typing a bound counts as engaging with price just as dragging does. */
  document.addEventListener('input', (event) => {
    const field = event.target;
    if (field.type !== 'number') return;
    const root = field.closest('[data-price-slider]');
    if (root) root.dataset.priceTouched = 'true';
  });

  function initPriceSliders() {
    document.querySelectorAll('[data-price-slider]').forEach((root) => syncPriceSlider(root, false));
  }
  document.addEventListener('DOMContentLoaded', initPriceSliders);
  document.addEventListener('neroli:facets:rendered', initPriceSliders);

  /* Delegated, because the sidebar is replaced wholesale on every filter
     update — a direct binding would survive exactly one change. */
  document.addEventListener('click', (event) => {
    const showMore = event.target.closest('[data-facet-show-more]');
    if (showMore) {
      const list = showMore.parentElement.querySelector('[data-facet-list]');
      if (!list) return;
      const expanded = list.classList.toggle('show-all');
      showMore.setAttribute('aria-expanded', String(expanded));
      showMore.textContent = expanded ? showMore.dataset.labelLess : showMore.dataset.labelMore;
      return;
    }

    /* Hiding is one-way from here: once the sidebar is gone this button goes
       with it, so the way back is the toolbar's filter button, which CSS
       reveals on desktop whenever .filters-hidden is set. */
    const hideFilters = event.target.closest('[data-toggle-filters]');
    if (hideFilters) {
      const shell = document.querySelector('[data-collection-shell]');
      if (shell) shell.classList.add('filters-hidden');
    }
  });

/* ---- from snippets/collection-sort.liquid ---- */
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-sort-select]');
    if (!select) return;
    const url = new URL(window.location.href);
    /* Choosing the default back again should drop the parameter rather than
       pin it into the URL, for the same reason as above. */
    if (select.value && select.value !== select.dataset.defaultSort) {
      url.searchParams.set('sort_by', select.value);
    } else {
      url.searchParams.delete('sort_by');
    }
    window.dispatchEvent(new CustomEvent('facets:update', { detail: { url: url.pathname + url.search } }));
  });

/* ---- from snippets/collection-subcategories.liquid ---- */
  document.addEventListener('click', (event) => {
    const next = event.target.closest('[data-subcategory-next]');
    if (!next) return;
    const track = next.parentElement.querySelector('[data-subcategory-track]');
    if (track) track.scrollBy({ left: track.clientWidth * 0.8, behavior: 'smooth' });
  });

  /* The arrow only appears when the row actually overflows — an arrow that
     scrolls nothing is worse than no arrow. */
  function syncSubcategoryArrows() {
    document.querySelectorAll('[data-subcategory-track]').forEach((track) => {
      const arrow = track.parentElement.querySelector('[data-subcategory-next]');
      if (!arrow) return;
      const overflows = track.scrollWidth > track.clientWidth + 4;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      arrow.hidden = !overflows || atEnd;
    });
  }

  document.addEventListener('DOMContentLoaded', syncSubcategoryArrows);
  window.addEventListener('resize', syncSubcategoryArrows, { passive: true });
  document.addEventListener('scroll', (event) => {
    if (event.target.matches && event.target.matches('[data-subcategory-track]')) syncSubcategoryArrows();
  }, true);

/* ---- from snippets/collection-hero.liquid ---- */
  class ReadMoreText extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-read-more-body]');
      this.toggle = this.querySelector('[data-read-more-toggle]');
      if (!this.body || !this.toggle) return;

      this.expanded = false;
      this.onResize = this.debounce(() => this.sync(), 200);

      // Fonts can still be loading at first paint, which changes how many
      // lines the text occupies — measure after a frame, and again once web
      // fonts settle.
      requestAnimationFrame(() => this.sync());
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.sync());
      }

      window.addEventListener('resize', this.onResize, { passive: true });
      this.toggle.addEventListener('click', () => this.setExpanded(!this.expanded));
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    /* Only offer the control when the text is genuinely clipped — a "Read
       more" that reveals nothing is worse than no control at all. */
    sync() {
      if (this.expanded) return;
      this.toggle.hidden = this.body.scrollHeight <= this.body.clientHeight + 1;
    }

    setExpanded(next) {
      this.expanded = next;
      this.body.classList.toggle('is-expanded', next);
      this.toggle.setAttribute('aria-expanded', String(next));
      this.toggle.textContent = next ? this.toggle.dataset.labelLess : this.toggle.dataset.labelMore;
    }

    debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), wait);
      };
    }
  }

  if (!customElements.get('read-more-text')) {
    customElements.define('read-more-text', ReadMoreText);
  }
